import { type TFunction } from "i18next"

import { budgetFormat } from "../../utils/number/budget-format.ts"
import type {
  ComparisonDiffEntry,
  ComparisonGroup,
  ComparisonGroupedValue,
  ComparisonGroupItem
} from "./comparison-grouped-value.types.ts"
import type {
  FieldComparison,
  MilestoneDuration,
  MilestoneEntry,
  PaymentEntry,
  PaymentSource,
  ScopeDiff,
  ScopeSource,
  SourceType
} from "./types.ts"

interface GroupedCellCopy {
  identicalMessage: string
  itemNounSingular: string
  itemNounPlural: string
}

interface GroupedComparisonMapping {
  groupedValues: Partial<Record<SourceType, ComparisonGroupedValue>>
  groupedCellCopy: GroupedCellCopy
}

function otherGroupName(t: TFunction): string {
  return t("documentSync.groupedComparison.otherGroup")
}

function formatAmountDuration(
  amount: string | undefined,
  duration: MilestoneDuration | null | undefined,
  t: TFunction
): string {
  const parts: string[] = []
  if (duration) {
    parts.push(
      t("documentSync.groupedComparison.durationDays", { count: duration.value })
    )
  }
  if (amount !== undefined) parts.push(budgetFormat(amount))
  return parts.join(" · ")
}

function milestoneToItem(
  milestone: MilestoneEntry,
  t: TFunction
): ComparisonGroupItem {
  return {
    id: milestone.id,
    label: milestone.name,
    detail: formatAmountDuration(milestone.amount, milestone.duration, t)
  }
}

/**
 * Mirrors `ScopeOfWorkBlockList`'s own grouping key/fallback
 * (`project_type_id || project_type || "unknown"`, name
 * `project_type_name || "Other"`) so the comparison cell's groups read the
 * same as the document editor's — never re-derived or flattened.
 */
function groupMilestones(source: ScopeSource, t: TFunction): ComparisonGroup[] {
  const order: string[] = []
  const groups = new Map<string, ComparisonGroup>()

  source.milestones.forEach((milestone) => {
    const groupId = milestone.project_type_id ?? "unknown"
    const groupName = milestone.project_type_name ?? otherGroupName(t)

    if (!groups.has(groupId)) {
      groups.set(groupId, { id: groupId, name: groupName, items: [] })
      order.push(groupId)
    }
    groups.get(groupId)!.items.push(milestoneToItem(milestone, t))
  })

  return order.map((id) => groups.get(id)!)
}

/**
 * `scope_diff`'s `changed` entries only carry the fields that actually
 * changed — an unchanged amount/duration is simply absent, not repeated.
 * `afterSource` (the structured milestones for whichever column the diff is
 * attached to) fills those gaps so `before`/`after` can each render as a
 * complete item, per `ComparisonDiffEntry`'s own contract.
 */
function buildScopeDiffEntries(
  diff: ScopeDiff | null | undefined,
  afterSource: ScopeSource | undefined,
  t: TFunction
): ComparisonDiffEntry[] | undefined {
  if (!diff) return undefined

  const afterById = new Map(
    (afterSource?.milestones ?? []).map((milestone) => [milestone.id, milestone])
  )

  const added: ComparisonDiffEntry[] = diff.added.map((milestone) => ({
    id: milestone.id,
    status: "added",
    groupName: milestone.project_type_name ?? otherGroupName(t),
    after: milestoneToItem(milestone, t)
  }))

  const removed: ComparisonDiffEntry[] = diff.removed.map((milestone) => ({
    id: milestone.id,
    status: "removed",
    groupName: milestone.project_type_name ?? otherGroupName(t),
    before: milestoneToItem(milestone, t)
  }))

  const changed: ComparisonDiffEntry[] = diff.changed.map((entry) => {
    const after = afterById.get(entry.id)
    const previousAmount = entry.old_amount ?? after?.amount
    const currentAmount = entry.new_amount ?? after?.amount
    const previousDuration =
      entry.old_duration !== undefined ? entry.old_duration : (after?.duration ?? null)
    const currentDuration =
      entry.new_duration !== undefined ? entry.new_duration : (after?.duration ?? null)
    // `name` is confirmed live to be absent entirely on a changed entry
    // whose name itself didn't change (a duration-only change arrives as
    // just `{id, old_duration, new_duration}`) — fall back to the
    // after-source milestone's real name rather than rendering a blank
    // label.
    const fallbackName = entry.name ?? after?.name ?? ""
    const previousName = entry.old_name ?? fallbackName
    const currentName = entry.new_name ?? fallbackName

    return {
      id: entry.id,
      status: "changed",
      groupName:
        entry.new_project_type_name ?? after?.project_type_name ?? otherGroupName(t),
      before: {
        id: entry.id,
        label: previousName,
        detail: formatAmountDuration(previousAmount, previousDuration, t)
      },
      after: {
        id: entry.id,
        label: currentName,
        detail: formatAmountDuration(currentAmount, currentDuration, t)
      }
    }
  })

  return [...added, ...removed, ...changed]
}

/**
 * `scope_structured` is real for every source that has a document (`lead`
 * included — a Lead's `project_phases` are structured too, unlike payment
 * terms); a `null` per-source is a genuine "nothing to structure" (no
 * linked Proposal), handled by the caller simply omitting that column from
 * `groupedValues` so `ComparisonTable` falls back to its plain
 * `values`/`FieldValueLines` rendering for that one cell.
 *
 * `diffColumnKey` names which single column the `scope_diff` belongs to.
 * Confirmed live (PR #1255, a real Contract sync-status call): `scope_diff`
 * is always Lead-relative — comparing the row's own document against the
 * Lead's milestones — regardless of which column that endpoint uses as its
 * plain-text comparison baseline (a Contract with a linked Proposal still
 * diffed against the Lead, not the Proposal, in the confirmed response).
 * `diffColumnKey` is therefore always "whichever column is this document
 * being generated" rather than "whichever column is the baseline" — the two
 * Generate modals know this unambiguously (their own document side, never
 * the baseline side). The 3-column Project-creation modal has no single
 * "document being generated" column, so it instead passes whichever of
 * Proposal/Contract the backend already recommends (`recommended_source`),
 * or omits it entirely when the recommendation is Lead itself — an
 * approximation, since the true diff is always against Lead regardless of
 * recommendation, kept only to avoid showing the affordance on a column
 * that isn't the one thing scope_diff is actually relative to.
 */
function mapScope(
  comparison: FieldComparison,
  columnKeys: SourceType[],
  t: TFunction,
  diffColumnKey: SourceType | undefined
): GroupedComparisonMapping | undefined {
  const structured = comparison.scope_structured
  if (!structured) return undefined

  const groupedValues: Partial<Record<SourceType, ComparisonGroupedValue>> = {}
  const diffEntries =
    diffColumnKey && structured[diffColumnKey]
      ? buildScopeDiffEntries(comparison.scope_diff, structured[diffColumnKey]!, t)
      : undefined

  columnKeys.forEach((key) => {
    const source = structured[key]
    if (!source) return

    groupedValues[key] = {
      groups: groupMilestones(source, t),
      diff: key === diffColumnKey ? diffEntries : undefined
    }
  })

  if (Object.keys(groupedValues).length === 0) return undefined

  return {
    groupedValues,
    groupedCellCopy: {
      identicalMessage: t("documentSync.groupedComparison.scopeIdenticalMessage"),
      itemNounSingular: t("documentSync.groupedComparison.milestoneNounSingular"),
      itemNounPlural: t("documentSync.groupedComparison.milestoneNounPlural")
    }
  }
}

function formatPaymentDetail(payment: PaymentEntry, t: TFunction): string {
  const parts: string[] = []
  const percentage = parseFloat(payment.percentage)
  if (!Number.isNaN(percentage)) parts.push(`${percentage}%`)
  parts.push(budgetFormat(payment.amount))
  if (payment.due_date) {
    const parsed = new Date(payment.due_date)
    if (!Number.isNaN(parsed.getTime())) {
      parts.push(
        t("documentSync.groupedComparison.dueDate", {
          date: parsed.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric"
          })
        })
      )
    }
  }
  return parts.join(" · ")
}

function paymentToItem(payment: PaymentEntry, t: TFunction): ComparisonGroupItem {
  return {
    id: payment.id,
    label: payment.name ?? t("documentSync.groupedComparison.unnamedPayment"),
    detail: formatPaymentDetail(payment, t)
  }
}

/** Payment Terms has no Project-Type concept of its own (`project_types` is
 * always `[]`) — one synthetic group, per `ComparisonGroup`'s own doc
 * comment on callers with nothing to group by. */
function groupPayments(source: PaymentSource, t: TFunction): ComparisonGroup[] {
  return [
    {
      id: "payment-schedule",
      name: t("documentSync.groupedComparison.paymentScheduleGroupName"),
      items: source.payments.map((payment) => paymentToItem(payment, t))
    }
  ]
}

/**
 * `payment_terms_structured.lead` is always `null` (`core.Project` has no
 * structured payment model) — that column is simply omitted from
 * `groupedValues`, so the Lead cell keeps rendering its existing plain-text
 * `lead_display` summary rather than a broken or empty grouped box.
 * `payment_terms_diff` is always `null` today, so no column here ever gets
 * a `diff` — `GroupedComparisonCell` renders no "View changes" affordance
 * as a result, by design (see `FieldComparison.payment_terms_diff`).
 */
function mapPaymentTerms(
  comparison: FieldComparison,
  columnKeys: SourceType[],
  t: TFunction
): GroupedComparisonMapping | undefined {
  const structured = comparison.payment_terms_structured
  if (!structured) return undefined

  const groupedValues: Partial<Record<SourceType, ComparisonGroupedValue>> = {}

  columnKeys.forEach((key) => {
    const source = structured[key]
    if (!source) return

    groupedValues[key] = { groups: groupPayments(source, t) }
  })

  if (Object.keys(groupedValues).length === 0) return undefined

  return {
    groupedValues,
    groupedCellCopy: {
      identicalMessage: t(
        "documentSync.groupedComparison.paymentTermsIdenticalMessage"
      ),
      itemNounSingular: t("documentSync.groupedComparison.paymentNounSingular"),
      itemNounPlural: t("documentSync.groupedComparison.paymentNounPlural")
    }
  }
}

/**
 * Adapts a raw `FieldComparison` row's new structured backend fields
 * (`scope_structured`/`scope_diff`, `payment_terms_structured`) into the
 * `groupedValues`/`groupedCellCopy` shape `ComparisonTable` renders via
 * `GroupedComparisonCell` — the one place all three Generate/Confirm-time
 * modals build this mapping, rather than three parallel implementations.
 *
 * Returns `undefined` for any field key other than `scope`/`payment_terms`,
 * and also when the row's structured field is itself `null` (an older
 * cached response that predates the backend's structured response, or a
 * field the backend genuinely has nothing structured for) — the caller
 * keeps building its row's plain `values` exactly as before in that case,
 * so this is purely additive.
 */
function mapFieldComparisonToGroupedValues(
  comparison: FieldComparison,
  columnKeys: SourceType[],
  t: TFunction,
  diffColumnKey?: SourceType
): GroupedComparisonMapping | undefined {
  if (comparison.key === "scope") {
    return mapScope(comparison, columnKeys, t, diffColumnKey)
  }
  if (comparison.key === "payment_terms") {
    return mapPaymentTerms(comparison, columnKeys, t)
  }
  return undefined
}

export { type GroupedComparisonMapping, mapFieldComparisonToGroupedValues }
